import { useIntersection } from '@mantine/hooks';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  CustomCloseReasons,
  DefaultConfirmCloseProps,
  DragModal,
  PossibleCloseEvent,
} from 'components/DragModal/DragModal';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import SkeletonBox from 'components/generic/SkeletonBox';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { SearchModal, SetSelectionsFn } from 'components/SearchModal/SearchModal';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TFunction } from 'i18next';
import {
  fiveMinsMs,
  useCosmosQueryClient,
} from 'providers/clients/CosmosQueryClientProvider';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  BroadcastChannelItem,
  DeleteBroadcastMessageIdParams,
  DeleteBroadcastMessageIdResult,
  GetBroadcastChannelParams,
  GetBroadcastChannelResult,
  GetBroadcastMessageIdResult,
  PostBroadcastMessageIdPublishParams,
  PostBroadcastMessageIdPublishResult,
  PostBroadcastMessageParams,
  PostBroadcastMessageResult,
  PutBroadcastMessageIdParams,
  PutBroadcastMessageIdResult,
} from 'providers/contexts/extensions/Broadcast';
import { GetEditorialAssetByIdResult } from 'providers/contexts/extensions/Editorial';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Controller,
  FormProvider,
  SubmitErrorHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'remeda';
import { bloxValues } from 'themes/constants';
import { safeMaxDateTime } from 'utility/constants';
import {
  cleanStringFromHtmlAndBloxInline,
  isValidUrl,
  shouldValidDirtyTouch,
} from 'utility/functions';
import {
  checkBroadcastFullAccessPermission,
  checkBroadcastSendNotificationsPermission,
  checkEditorialAccessPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { getInputErrorTextSx } from 'utility/styles';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  EditorialAssetSearchResultItem,
} from 'utility/types/dataProvider';
import { NotifierContextType, useNotifierModalContext } from './NotifierModalContext';
import { NotifierStatusBadge } from './NotifierStatusBadge';

// Extend dayjs with UTC plugin
dayjs.extend(utc);

type TranslatedStrings = {
  loading: string;
  error: string;
  title: string;
  delete: string;
  deleted: string;
  edit: string;
  select: string;
  search: string;
  view: string;
  date: string;
  scheduledDateAndTime: string;
  updated: string;
  target: string;
  targetInfo: string;
  created: string;
  promptDelete: string;
  cannotUndo: string;
  draftAssetDisabled: string;
  saveDraft: string;
  schedulePost: string;
  channels: string;
  channelDisabled: string;
  createPost: string;
  editingPost: string;
  viewingPost: string;
  message: string;
  messagePublished: string;
  endOfResults: string;
  formInvalid: string;
  noChannels: string;
  openInNewWindow: string;
  validTargetRequired: string;
  channelsRequired: string;
  invalidStartTime: string;
};

type GetNotifierTranslationsArgs = {
  t: TFunction;
};

export function getNotifierTranslations({
  t,
}: GetNotifierTranslationsArgs): TranslatedStrings {
  const translations: TranslatedStrings = {
    loading: t('blox.general.loading'),
    error: t('blox.general.error'),
    title: t('blox.general.title'),
    delete: t('blox.actions.delete'),
    deleted: t('blox.general.deleted'),
    edit: t('blox.actions.edit'),
    view: t('blox.actions.view'),
    date: t('blox.general.date.label'),
    scheduledDateAndTime: t('blox.notifier.scheduled_date_and_time'),
    updated: t('blox.general.updated'),
    created: t('blox.general.created'),
    select: t('blox.actions.select'),
    search: t('blox.general.search'),
    target: t('blox.general.target'),
    targetInfo: t('blox.notifier.target_info'),
    promptDelete: t('blox.modal.prompt_delete_basic'),
    cannotUndo: t('blox.modal.no_undo'),
    draftAssetDisabled: t('blox.notifier.draft_asset_disabled'),
    saveDraft: t('blox.notifier.save_draft'),
    schedulePost: t('blox.notifier.schedule_post'),
    channels: t('blox.notifier.channels'),
    channelDisabled: t('blox.notifier.channel_disabled'),
    createPost: t('blox.notifier.create_post'),
    editingPost: t('blox.notifier.editing_post'),
    viewingPost: t('blox.notifier.viewing_post'),
    message: t('blox.general.message.label'),
    messagePublished: t('blox.notifier.message_published'),
    endOfResults: t('blox.general.end_of_results'),
    formInvalid: t('blox.validation.form_invalid'),
    noChannels: t('blox.notifier.no_channels_found'),
    openInNewWindow: t('blox.actions.open_in_new_window'),
    validTargetRequired: t('blox.notifier.validation.valid_target_required'),
    channelsRequired: t('blox.notifier.validation.publish_channels_required'),
    invalidStartTime: t('blox.notifier.validation.date_invalid_start_time'),
  };
  return translations;
}

/**
 * Edit / Create Notifier (broadcast) Message Modal
 *
 * Shares most similarity with NotiferToolkit.tsx modal
 * but is not hooked into the Asset screen contexts.
 * Some similar common code is pulled in from main modal.
 *
 * This will eventually contain more data and fields
 * as compared to the asset edit notifier toolkit modal.
 *
 * When creating while in existing article
 * set no target, use asset_uuid + asset_app combo
 *
 * specifically:
 * need to use asset_uuid + asset_app together
 * OR just target (as a url). you should be able
 * for example either of these should be okay:
 * { ... uuid: '1234', app: 'editorial', target: null }
 * { uuid: null, app: null, target: 'https://example.com' }
 *
 */

// Notifier Message Item / RHF Fields Type
// this basically is the body of the put/post req
// for put (updates) set item id to the PATH params

export type NotifierModalSaveMode = 'draft' | 'publish';
export type BroadcastMsgPutBody = PutBroadcastMessageIdParams['body'];
export type BroadcastItemInputsType = {
  title?: BroadcastMsgPutBody['title'];
  message?: BroadcastMsgPutBody['message'];
  social_title?: BroadcastMsgPutBody['social_title'];
  social_summary?: BroadcastMsgPutBody['social_summary'];
  send_date?: BroadcastMsgPutBody['send_date'];
  channels?: BroadcastMsgPutBody['channels'];
  asset_uuid?: BroadcastMsgPutBody['asset_uuid'];
  asset_app?: BroadcastMsgPutBody['asset_app'];
  target?: BroadcastMsgPutBody['target'];
};

/**
 * Helper to format form data to put/post body.
 *
 * Note: If using asset_uuid + asset_app, target should be null.
 * The API will set the "Asset:..." string automatically.
 * Dont send an empty string, API will save that...
 */
export function buildNotifierBodyPayload(
  data: Partial<BroadcastItemInputsType>,
  mode: NotifierModalSaveMode,
): BroadcastItemInputsType {
  const hasApp = Boolean(data?.asset_app);
  const hasUuid = Boolean(data?.asset_uuid);
  const isUsingAsset = hasApp && hasUuid;

  // Normalize and clean fields
  const nTarget = data?.target?.trim() || null;
  const finalTarget = isUsingAsset ? null : nTarget;

  const nTitle = data?.title?.trim() || null;
  const nMessage = data?.message?.trim() || null;

  const cleaned: BroadcastItemInputsType = {
    title: nTitle,
    message: nMessage,
    send_date: data?.send_date,
    channels: data?.channels ?? [],
    target: finalTarget,
    asset_uuid: data?.asset_uuid,
    asset_app: data?.asset_app,
  };
  return cleaned;
}

export const NOTIFIER_MSG_MAX_LENGTH = 250;

/**
 * Notifier Message Modal Props
 * Available via useNotifierModalContext hook
 */
export type NotifierMessageModalProps = {
  msgModalId: NotifierContextType['msgModalId']; // "create" or message ID
  msgModalMode: NotifierContextType['msgModalMode'];
  msgModalOpen: NotifierContextType['msgModalOpen'];
  handleCloseMsgModal: NotifierContextType['handleCloseMsgModal'];
};

export const NotifierMessageModal = memo(
  ({
    msgModalId,
    msgModalMode,
    msgModalOpen,
    handleCloseMsgModal,
  }: NotifierMessageModalProps) => {
    const theme = useTheme();
    const { activeSite, sessionConfig, userInfo } = useCosmos();
    const { queryClient } = useCosmosQueryClient();
    const notify = useNotify();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const siteTimeZone = sessionConfig?.time_zone;
    const { t } = useTranslation();

    // Translation strings
    const tlns = useMemo(() => {
      return getNotifierTranslations({ t: t });
    }, [t]);

    // Permission Checks
    const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
    const { granted: userHasFullBroadcast } =
      checkBroadcastFullAccessPermission(sessionConfig);
    const { granted: userHasSendBroadcast, name: sendBroadcastPermName } =
      checkBroadcastSendNotificationsPermission(sessionConfig);

    // Editorial checks for assets modal, button, validation.
    const { granted: userHasEditorial, name: editorialAccessPerm } =
      checkEditorialAccessPermission(sessionConfig);
    const hasEditorialPerms = Boolean(userIsAdmin || userHasEditorial);

    // Convenience variables for mode
    const isEditMode = msgModalMode === 'edit';
    const isCreateMode = msgModalMode === 'create';

    /**
     * Query - Get broadcast message by ID (edit mode)
     */
    const {
      data: fetchedMsg,
      status: getIdStatus,
      error: getIdError,
      refetch: getIdRefetch,
    } = useQuery<
      GetBroadcastMessageIdResult,
      ApiErrorResponse,
      GetBroadcastMessageIdResult['data']
    >({
      queryKey: ['getBroadcastMessageId', msgModalId],
      queryFn: async () => {
        if (!msgModalId) {
          throw new Error('Message ID is required.');
        }
        if (msgModalId === 'create') {
          throw new Error('Create modal ID recieved.');
        }
        const result = await dataProvider.getBroadcastMessageId({
          path: { id: msgModalId },
        });
        return result;
      },
      enabled: Boolean(isEditMode && msgModalId && msgModalOpen),
      select: (data) => data?.data,
      gcTime: 0, // no bg/auto refetch, fresh every open
      staleTime: 0, // manual invalidate / refetch only
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    // Message id from fetched data
    const messageId = fetchedMsg?.message_id;

    // Delivery logs modal opener (CMSADMIN-2626)
    const { handleOpenLogsModal } = useNotifierModalContext();

    // Delivery logs only exist once a send has been attempted, and the logs
    // API is staff-only. Surface the "View Logs" entry point for staff users
    // on terminal/attempted statuses.
    const showViewLogs = useMemo(() => {
      const status = fetchedMsg?.send_status;
      return (
        userInfo?.account_type === 'staff' &&
        isEditMode &&
        Boolean(messageId) &&
        (status === 'sent' ||
          status === 'failed' ||
          status === 'incomplete' ||
          status === 'undelivered')
      );
    }, [isEditMode, messageId, fetchedMsg?.send_status, userInfo?.account_type]);

    /**
     * Infinite Query - Broadcast Channels
     */
    const {
      data: channelsData,
      status: channelsInfQueryStatus,
      hasNextPage: hasNextPageChannels,
      isFetchingNextPage: isFetchingNextPageChannels,
      fetchNextPage: fetchNextPageChannels,
    } = useInfiniteQuery<GetBroadcastChannelResult, ApiErrorResponse>({
      queryKey: ['getBroadcastChannel'],
      queryFn: async (context) => {
        const { pageParam = '' } = context;
        const payload: GetBroadcastChannelParams = {
          query: {
            page: pageParam as any,
            limit: 50,
          },
        };
        const result = await dataProvider.getBroadcastChannel(payload);
        return result;
      },
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.data?.next;
      },
      getPreviousPageParam: (lastPage, allPages) => {
        return lastPage?.data?.prev;
      },
      enabled: Boolean(msgModalOpen),
      gcTime: fiveMinsMs,
      staleTime: fiveMinsMs,
      retry: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialPageParam: '',
    });

    // flat notifier channels from query pages
    const flatChannels = useMemo<BroadcastChannelItem[]>(() => {
      if (!channelsData) {
        return [];
      }
      const allItems = channelsData?.pages.flatMap((page) => page?.data?.items || []);
      return allItems;
    }, [channelsData]);

    // loading states for channels
    const isChannelsInitialLoading = channelsInfQueryStatus === 'pending';
    const noChannels = flatChannels?.length === 0;

    /**
     * put mutation - Update existing broadcast message
     */
    const { mutateAsync: mutateUpdate, status: mutateUpdateStatus } = useMutation<
      PutBroadcastMessageIdResult,
      ApiErrorResponse,
      PutBroadcastMessageIdParams
    >({
      mutationKey: ['putBroadcastMessageId'],
      mutationFn: async (params) => {
        return await dataProvider.putBroadcastMessageId(params);
      },
      onError: (error) => {
        notify(`${tlns.error}: ${error?.message || error?.axiosMessage}`, {
          type: 'error',
        });
      },
    });

    /**
     * post mutation - Create new broadcast message
     */
    const { mutateAsync: mutateCreate, status: mutateCreateStatus } = useMutation<
      PostBroadcastMessageResult,
      ApiErrorResponse,
      PostBroadcastMessageParams
    >({
      mutationKey: ['postBroadcastMessage'],
      mutationFn: async (params) => {
        return await dataProvider.postBroadcastMessage(params);
      },
      onError: (error) => {
        notify(`${tlns.error}: ${error?.message || error?.axiosMessage}`, {
          type: 'error',
        });
      },
    });

    /**
     * POST PUBLISH - broadcast message by ID
     */
    const { mutateAsync: mutatePublish, status: mutatePublishStatus } = useMutation<
      PostBroadcastMessageIdPublishResult,
      ApiErrorResponse,
      PostBroadcastMessageIdPublishParams
    >({
      mutationKey: ['postBroadcastMessageIdPublish'],
      mutationFn: async (params) => {
        return await dataProvider.postBroadcastMessageIdPublish(params);
      },
      onError: (error) => {
        notify(`${tlns.error}: ${error?.message || error?.axiosMessage}`, {
          type: 'error',
        });
      },
    });

    /**
     * Delete Mutation - broadcast message by ID
     */
    const { mutateAsync: mutateDelete, status: mutateDeleteStatus } = useMutation<
      DeleteBroadcastMessageIdResult,
      ApiErrorResponse,
      DeleteBroadcastMessageIdParams
    >({
      mutationKey: ['deleteBroadcastMessageId'],
      mutationFn: async (params) => {
        return await dataProvider.deleteBroadcastMessageId(params);
      },
      onError: (error) => {
        notify(`${tlns.error}: ${error?.message || error?.axiosMessage}`, {
          type: 'error',
        });
      },
    });

    // intersection observer for channels infinite scroll
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    // any mutations ongoing
    const anyMutating = [
      mutateUpdateStatus,
      mutateCreateStatus,
      mutatePublishStatus,
      mutateDeleteStatus,
    ].some((status) => status === 'pending');

    // any queries ongoing
    // In create mode getIdStatus should be ignored since the query is disabled
    // In React Query v5 disabled queries can show 'pending' status initially
    const anyQuerying = isEditMode
      ? [getIdStatus, channelsInfQueryStatus].some((status) => status === 'pending')
      : [channelsInfQueryStatus].some((status) => status === 'pending');

    // any broadcast network activity loading
    const anyLoading = Boolean(anyMutating || anyQuerying);

    /**
     * Mask if get by id is loading...
     * or we are PUT/POST/DELETE mutating
     */
    const isMasked =
      Boolean(isEditMode && getIdStatus === 'pending') ||
      mutateUpdateStatus === 'pending' ||
      mutateCreateStatus === 'pending' ||
      mutatePublishStatus === 'pending' ||
      mutateDeleteStatus === 'pending';

    // react-hook-form setup
    // note: there are more fields than the defaults here
    // notes regarding uuid/app vs target (url/ui string)
    const formDv: BroadcastItemInputsType = useMemo(() => {
      const defaults: BroadcastItemInputsType = {
        title: '',
        message: '',
        send_date: '',
        channels: [],
        asset_uuid: null,
        asset_app: null,
        target: null,
      };
      return defaults;
    }, []);

    // setup rhf methods
    const methods = useForm<BroadcastItemInputsType>({
      defaultValues: formDv,
      mode: 'all',
      reValidateMode: 'onChange',
    });

    const {
      register,
      reset,
      handleSubmit,
      control,
      getFieldState,
      trigger,
      formState,
      getValues,
      setError,
      setValue,
    } = methods;

    // set inputs ready once the reset/fetch data is set to form
    const [defaultsLoaded, setDefaultsLoaded] = useState(false);

    /**
     * Controlled DayJS state for date picker.
     * As a separate state from the RHF value to
     * prevent clearing the input on partial edits
     * and not have the value jump around as we
     * convert the timezone back and forth.
     */
    const [sendDateState, setSendDateState] = useState<Dayjs | null>(null);

    /**
     * State for an assets start time, for date validation (eg min date).
     * Will be set via fetch initially if set with an asset UUID
     * and updated on asset select to avoid extra fetches.
     */
    const [assetStartTime, setAssetStartTime] = useState<string | null>(null);

    // Effects to handle form state setup
    useEffect(() => {
      if (isCreateMode) {
        // blank form state, set inputs ready
        reset(formDv);
        setSendDateState(null);
        setDefaultsLoaded(true);
        return;
      }

      const editReadyLoaded =
        isEditMode && Boolean(fetchedMsg) && getIdStatus === 'success';

      // edit mode - fetched by id - fill with fetched data
      if (editReadyLoaded) {
        // format channels as post body wants (just ids array)
        const channels = Array.isArray(fetchedMsg?.channels) ? fetchedMsg?.channels : [];
        const channelIds = channels.map((channel) => channel?.id ?? '').filter(Boolean);
        // set fields from fetched data...
        const loadFields: BroadcastItemInputsType = {
          title: fetchedMsg?.title ?? formDv?.title,
          message: fetchedMsg?.message ?? formDv?.message,
          send_date: fetchedMsg?.send_date ?? formDv?.send_date,
          channels: channelIds,
          asset_uuid: fetchedMsg?.asset_uuid ?? formDv?.asset_uuid,
          asset_app: fetchedMsg?.asset_app ?? formDv?.asset_app,
          target: fetchedMsg?.target ?? formDv?.target,
        };

        // set dayjs state for date picker
        // no value is null, else parse in site timezone
        const hasSendDate = Boolean(loadFields?.send_date);
        const parsedSendDate = hasSendDate
          ? dayjs.utc(loadFields.send_date).tz(siteTimeZone)
          : null;

        // set form with loaded data, inputs ready
        reset(loadFields);
        setSendDateState(parsedSendDate);
        setDefaultsLoaded(true);

        return;
      }
    }, [fetchedMsg, formDv, getIdStatus, isCreateMode, isEditMode, reset, siteTimeZone]);

    // form / states / ui labels
    const updatedOrCreated = isEditMode ? tlns.updated : tlns.created;
    const fsTitle = getFieldState('title', formState);
    const fsMessage = getFieldState('message', formState);
    const wMessage = useWatch({ control: control, name: 'message' });
    const msgLength = wMessage?.length || 0;
    const msgLabel = `${tlns.message} (${msgLength}/${NOTIFIER_MSG_MAX_LENGTH})`;
    const fsChannels = getFieldState('channels', formState);
    const wChannels = useWatch({ control: control, name: 'channels' });
    const fsSendDate = getFieldState('send_date', formState);
    const wSendDate = useWatch({ control: control, name: 'send_date' });
    const wAssetUuid = useWatch({ control: control, name: 'asset_uuid' });
    const viewAssetHref = wAssetUuid ? `/${activeSite}/editorial/edit/${wAssetUuid}` : '';
    const fsTarget = getFieldState('target', formState);
    const formIsValid = formState.isValid;

    const noPerms = useMemo(() => {
      return !userIsAdmin && !userHasFullBroadcast && !userHasSendBroadcast;
    }, [userIsAdmin, userHasFullBroadcast, userHasSendBroadcast]);

    const isSentItem = useMemo(() => {
      return Boolean(fetchedMsg?.send_status && fetchedMsg?.send_status === 'sent');
    }, [fetchedMsg?.send_status]);

    const isReadOnly = useMemo(() => {
      return isSentItem || noPerms;
    }, [isSentItem, noPerms]);

    // Per PR comments change request, dates
    // need to validate on load as blue allowed
    // for bad dates in the past, so we need to
    // validate the entire thing on load now.
    useEffect(() => {
      // skip if read only, dont run validation
      if (isReadOnly) {
        return;
      }
      const asyncTrigger = async () => await trigger();
      if (defaultsLoaded && isEditMode) {
        asyncTrigger();
      }
    }, [defaultsLoaded, isEditMode, isReadOnly, trigger]);

    // Close modal and reset form states helper
    const closeAndResetStates = useCallback(() => {
      reset(formDv);
      setDefaultsLoaded(false);
      setSendDateState(null);
      setAssetStartTime(null);
      handleCloseMsgModal();
    }, [formDv, handleCloseMsgModal, reset]);

    // form is valid, save as draft and maybe post id to publish
    const onValidSubmit = async (
      data: BroadcastItemInputsType,
      mode: NotifierModalSaveMode, // 'publish' | 'draft'
    ) => {
      try {
        const fmtBody = buildNotifierBodyPayload(data, mode);

        // guard since typecasting create (body msg required)
        if (!isEditMode && !fmtBody?.message?.trim().length) {
          throw new Error('Message is required when creating a new post.');
        }

        // update existing draft or create new message
        const result = isEditMode
          ? await mutateUpdate({
              path: { id: String(messageId) },
              body: fmtBody,
            })
          : await mutateCreate({
              body: fmtBody as PostBroadcastMessageParams['body'],
            });

        // confirm api response
        if (result?.status !== 'success') {
          throw new Error('Status returned was not success.');
        }

        // if save draft we only need to toast notify the action
        // if publishing, call mutatePublish with the id after draft save
        switch (mode) {
          case 'draft': {
            notify(`${tlns.message} ${updatedOrCreated}`, { type: 'success' });
            break;
          }
          case 'publish': {
            const publishResult = await mutatePublish({
              path: { id: String(result?.data?.message_id) },
            });
            if (publishResult?.status === 'success') {
              notify(tlns.messagePublished, { type: 'success' });
            } else {
              throw new Error('Status returned was not success.');
            }
            break;
          }
          default: {
            throw new Error(`Invalid mode: ${mode}`);
          }
        }

        // Invalidate getList data calls to refresh search...
        queryClient.invalidateQueries({ queryKey: ['broadcast/message'] });
        queryClient.invalidateQueries({ queryKey: ['getBroadcastMessage'] });
        closeAndResetStates();
      } catch (error) {
        const castError = error as ApiErrorResponse;
        const errorMessage = castError?.message || castError?.axiosMessage;
        notify(`${tlns.error}: ${errorMessage}`, { type: 'error' });
      }
    };

    // invalid form submission attempt
    const onInvalidSubmit: SubmitErrorHandler<BroadcastItemInputsType> = async (
      errors,
      event,
    ) => {
      console.warn('[onInvalidSubmit] form invalid:', errors);
      notify(tlns.formInvalid, { type: 'error' });
      return;
    };

    /**
     * Handle form save button clicks (draft or publish)
     */
    const handleFormSave = async (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      mode: NotifierModalSaveMode,
    ) => {
      e?.preventDefault();

      // Target can be used directly with a URL
      // or with asset_uuid and asset_app fields.

      // If target input is a valid URL, this takes precedence
      // and clears the asset fields. If target is not a valid URL,
      // then asset_uuid and asset_app both must be set.

      // Cannot send both target and asset fields together.
      // API errors: "May only contain target or asset_uuid and asset_app"

      const currentTarget = getValues('target') || '';
      const currentApp = getValues('asset_app') || '';
      const currentUuid = getValues('asset_uuid') || '';
      const isValidTargetUrl = isValidUrl(currentTarget, ['http:', 'https:']);

      // Validate target field
      if (isValidTargetUrl) {
        // Valid URL - clear asset fields
        setValue('asset_uuid', null);
        setValue('asset_app', null);
      } else {
        // Not a valid URL - require asset_uuid and asset_app
        if (!currentUuid || !currentApp) {
          setError('target', {
            type: 'manual',
            message: tlns.validTargetRequired,
          });
          return;
        }
        // Valid asset fields - null target field.
        // Null so API sets "Asset:..." string for us.
        setValue('target', null);
      }

      // note: there is extra validation on the API side,
      // when publishing at least one channel must be selected.
      // however for draft saves, channels are not required.
      if (mode === 'publish') {
        await trigger('channels'); // rerun field level validation
        const channelsVal = getValues('channels');
        // ensure at least one channel is selected
        const isValidChannels =
          Array.isArray(channelsVal) && Boolean(channelsVal?.length);
        if (!isValidChannels) {
          setError('channels', {
            type: 'manual',
            message: tlns.channelsRequired,
          });
          return;
        }
      }

      // delegate to rhf handleSubmit
      await handleSubmit((data) => onValidSubmit(data, mode), onInvalidSubmit)();
    };

    // handle cleanup and close of modal
    const confirmIfDirty = Boolean(formState.isDirty);

    // Handle modal close events
    const handleModalClose = useCallback(
      (event: PossibleCloseEvent, reason?: CustomCloseReasons) => {
        // non dirty close - no confirmation needed
        if (!confirmIfDirty && reason === 'closeButton') {
          closeAndResetStates();
          return;
        }
        // dirty close - ask confirmation
        if (confirmIfDirty && reason === 'confirmClose') {
          closeAndResetStates();
          return;
        }
      },
      [confirmIfDirty, closeAndResetStates],
    );

    // Title for the modal - viewing, editing, or creating post
    const uiTitle = useMemo(() => {
      if (isReadOnly) {
        return tlns.viewingPost;
      }
      if (isEditMode) {
        return tlns.editingPost;
      }
      return tlns.createPost;
    }, [isReadOnly, isEditMode, tlns.viewingPost, tlns.editingPost, tlns.createPost]);

    /**
     * Asset Select Modal states and handlers
     */

    const [assetModalOpen, setAssetModalOpen] = useState(false);
    const [selectedAssets, setSelectedAssets] = useState<
      EditorialAssetSearchResultItem[]
    >([]);

    const handleClearItems = useCallback(() => {
      setSelectedAssets([]);
    }, []);

    const handleOpenAssetModal = useCallback(() => {
      handleClearItems();
      setAssetModalOpen(true);
    }, [handleClearItems]);

    const handleCloseAssetModal = useCallback(() => {
      handleClearItems();
      setAssetModalOpen(false);
    }, [handleClearItems]);

    // State handler for asset selections
    const handleSetSelections = useCallback<SetSelectionsFn>(
      (value) => {
        const items = typeof value === 'function' ? value(selectedAssets) : value;
        setSelectedAssets(items);
      },
      [selectedAssets],
    );

    /**
     * Handle asset accept from asset select modal
     */
    const handleAssetAccept = useCallback(
      (
        items: EditorialAssetSearchResultItem[],
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      ) => {
        // Get the last item selected (single select)
        const assetItem = items[items.length - 1] ?? null;

        // Nothing selected, close modal
        if (!assetItem) {
          handleCloseAssetModal();
          return;
        }

        // Handle updating main form values
        // Set the target to "Asset: {title}"
        const assetId = assetItem?.uuid || '';
        const assetTitle = assetItem?.title || '';
        setValue('asset_uuid', assetId, shouldValidDirtyTouch);
        setValue('asset_app', 'editorial', shouldValidDirtyTouch);
        setValue('target', `Asset: ${assetTitle}`, shouldValidDirtyTouch);

        // Autofill fields (if they are empty)
        const currentTitle = getValues('title') || '';
        if (!currentTitle.trim()) {
          setValue('title', assetTitle, shouldValidDirtyTouch);
        }

        // Autofill message if empty
        const currentMessage = getValues('message') || '';
        if (!currentMessage.trim()) {
          const assetSummary = cleanStringFromHtmlAndBloxInline(assetItem?.summary || '');
          const truncated = assetSummary.slice(0, NOTIFIER_MSG_MAX_LENGTH) || '';
          setValue('message', truncated, shouldValidDirtyTouch);
        }

        // Set the start_time states for date validations
        const currentSendDate = getValues('send_date') || null;
        const assetStart = assetItem?.start_time || null;
        setAssetStartTime(assetStart);

        // Autofill send_date if empty (to asset start_time)
        // Update both form and displayed dayjs states
        if (!currentSendDate && assetStart) {
          const assetStartDjs = dayjs.utc(assetStart).tz(siteTimeZone);
          setSendDateState(assetStartDjs);
          setValue('send_date', assetStart, shouldValidDirtyTouch);
        }

        // Close modal and clear selection state
        handleCloseAssetModal();
      },
      [getValues, handleCloseAssetModal, setValue, siteTimeZone],
    );

    /**
     * Date Field & Validations
     *
     * Note that technically, a user could have access to
     * notifier, but not editorial. So per teams meeting,
     * if the user is able to access editorial assets,
     * go ahead and use that as the min date for the notifier.
     * eg: cannot be before the asset start time.
     *
     * However, in the case the user cannot access editorial,
     * we cannot fetch the asset data to get the start time,
     * so this validation will be skipped.
     */

    const getEditorialAssetByIdQuery = useQuery<
      GetEditorialAssetByIdResult,
      ApiErrorResponse,
      GetEditorialAssetByIdResult['data']
    >({
      queryKey: ['getEditorialAssetById', wAssetUuid],
      queryFn: async () => {
        const result = await dataProvider.getEditorialAssetById({
          assetId: String(wAssetUuid),
        });
        return result;
      },
      enabled: Boolean(hasEditorialPerms && wAssetUuid && msgModalOpen),
      select: (data) => data?.data,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    const fetchedStartTime = getEditorialAssetByIdQuery.data?.start_time || null;
    const isAssetLoading = getEditorialAssetByIdQuery.isLoading;

    // Set assets start time to state from fetched data
    useEffect(() => {
      if (isAssetLoading) {
        return;
      }
      if (fetchedStartTime) {
        setAssetStartTime(fetchedStartTime);
      }
    }, [fetchedStartTime, isAssetLoading]);

    // Trigger validation of send_date field
    // when asset start_time state is changed
    useEffect(() => {
      if (isReadOnly) {
        return;
      }
      if (assetStartTime) {
        trigger('send_date');
      }
    }, [assetStartTime, isAssetLoading, isReadOnly, trigger]);

    // Minimum selectable date for date picker prop
    const assetStartDayJs = useMemo<Dayjs | undefined>(() => {
      if (!assetStartTime) {
        return undefined;
      }
      const startDjs = dayjs.utc(assetStartTime).tz(siteTimeZone);
      return startDjs;
    }, [assetStartTime, siteTimeZone]);

    // Is form send_date in the past?
    const isDateInPast = useMemo(() => {
      if (!wSendDate) return false;
      const parsed = dayjs.utc(wSendDate).tz(siteTimeZone);
      const nowSiteTz = dayjs().tz(siteTimeZone);
      return parsed.isBefore(nowSiteTz);
    }, [wSendDate, siteTimeZone]);

    // Validate send date form string value
    const validateSendDate = useCallback(
      (value: string | null | undefined) => {
        // A value is required
        if (!value) {
          return t('blox.validation.value_required', { label: tlns.date });
        }
        // Parse UTC ISO string - to site timezone for validations
        const parsed = dayjs.utc(value).tz(siteTimeZone);
        if (!parsed.isValid()) {
          return t('blox.validation.value_invalid', { label: tlns.date });
        }
        // Validate send_date vs assets start_time.
        // Asset start_time state should only be present if the user had
        // editorial access, to fetch the asset data. So if the user
        // did not have editorial perms, we skip this validation,
        // as they would not be able to have selected or fetched an asset.
        if (wAssetUuid && assetStartTime) {
          const assetStart = dayjs.utc(assetStartTime).tz(siteTimeZone);
          if (parsed.isBefore(assetStart)) {
            return tlns.invalidStartTime;
          }
        }
        // Valid
        return true;
      },
      [wAssetUuid, assetStartTime, siteTimeZone, t, tlns.date, tlns.invalidStartTime],
    );

    // Get date field help text
    const dateHelpText = useMemo(() => {
      if (isReadOnly) {
        return undefined;
      }
      if (fsSendDate?.error?.message) {
        return fsSendDate.error.message;
      }
      if (isDateInPast) {
        return t('blox.notifier.validation.date_in_past_warning');
      }
      return undefined;
    }, [fsSendDate?.error?.message, isDateInPast, isReadOnly, t]);

    /**
     * Delete button state and logic
     */

    // confirm delete dialog state
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const closeConfirmDeleteModal = useCallback(() => {
      setConfirmDeleteOpen(false);
    }, []);

    const handleDeleteMenuClick = useCallback((event?: React.MouseEvent<HTMLElement>) => {
      setConfirmDeleteOpen(true);
    }, []);

    // confirm delete action in dialog - try deleting via api
    const handleDeleteConfirm = useCallback(async () => {
      try {
        await mutateDelete({ path: { id: String(fetchedMsg?.message_id) } });
        notify(tlns.deleted, { type: 'success' });
        // Invalidate react-admin lists query
        queryClient.invalidateQueries({ queryKey: ['broadcast/message'] });
        queryClient.invalidateQueries({ queryKey: ['getBroadcastMessage'] });
        closeConfirmDeleteModal();
        closeAndResetStates();
      } catch (error) {
        const castError = error as ApiErrorResponse;
        const errNotif = `${tlns.error}: ${
          castError?.message || castError?.axiosMessage
        }`;
        notify(errNotif, { type: 'error' });
      }
    }, [
      closeConfirmDeleteModal,
      fetchedMsg?.message_id,
      closeAndResetStates,
      mutateDelete,
      notify,
      queryClient,
      tlns.deleted,
      tlns.error,
    ]);

    /**
     * UI Permissions / Button States
     */

    // General permissions / form states
    const permStates: PermissionInfo = useMemo(() => {
      // disabled during init setup, loading, fetch errors, etc...
      if (!defaultsLoaded || anyLoading || getIdError) {
        return {
          info: undefined,
          disabled: true,
        };
      }
      // disable inputs in read only mode
      if (isReadOnly) {
        return {
          info: undefined,
          disabled: true,
        };
      }
      // site admin or full/modify broadcast permissions
      if (userIsAdmin || userHasFullBroadcast || userHasSendBroadcast) {
        return {
          info: undefined,
          disabled: false,
        };
      }
      // user does not have modify broadcast permissions
      return {
        info: t('blox.permissions.msg.required_short', {
          name: sendBroadcastPermName,
        }),
        disabled: true,
      };
    }, [
      isReadOnly,
      anyLoading,
      defaultsLoaded,
      getIdError,
      sendBroadcastPermName,
      t,
      userHasFullBroadcast,
      userHasSendBroadcast,
      userIsAdmin,
    ]);

    // Delete button state
    const deleteBtnState: PermissionInfo = useMemo(() => {
      // sent items cannot be deleted
      if (isSentItem) {
        return {
          info: t('blox.notifier.status_undeletable', {
            status: capitalize(fetchedMsg?.send_status || ''),
          }),
          disabled: true,
        };
      }
      // return general perm states
      return permStates;
    }, [fetchedMsg?.send_status, isSentItem, permStates, t]);

    // Schedule Post button state (PUBLISH)
    const schedulePostBtnState: PermissionInfo = useMemo(() => {
      // block if asset is fetching (for date validation)
      if (isAssetLoading) {
        return {
          disabled: true,
          info: tlns.loading,
        };
      }
      // sent items cannot be modified
      if (isSentItem) {
        return {
          info: t('blox.notifier.status_uneditable', {
            status: capitalize(fetchedMsg?.send_status || ''),
          }),
          disabled: true,
        };
      }
      // form must be valid to proceed
      if (!formIsValid) {
        return {
          disabled: true,
          info: t('blox.validation.form_invalid'),
        };
      }
      // at least one channel must be selected
      const hasChannels = Array.isArray(wChannels) && Boolean(wChannels?.length);
      if (!hasChannels) {
        return {
          disabled: true,
          info: t('blox.notifier.validation.publish_channels_required'),
        };
      }
      // return general perm states
      return permStates;
    }, [
      fetchedMsg?.send_status,
      formIsValid,
      isAssetLoading,
      isSentItem,
      permStates,
      t,
      tlns.loading,
      wChannels,
    ]);

    // Save Draft button state
    const saveDraftBtnState: PermissionInfo = useMemo(() => {
      // Block if an asset is fetching (for date validation)
      if (isAssetLoading) {
        return {
          disabled: true,
          info: tlns.loading,
        };
      }
      // sent items cannot be modified
      if (isSentItem) {
        return {
          info: t('blox.notifier.status_uneditable', {
            status: capitalize(fetchedMsg?.send_status || ''),
          }),
          disabled: true,
        };
      }
      // form must be valid to proceed
      if (!formIsValid) {
        return {
          disabled: true,
          info: t('blox.validation.form_invalid'),
        };
      }
      // return general perm states
      return permStates;
    }, [
      fetchedMsg?.send_status,
      formIsValid,
      isAssetLoading,
      isSentItem,
      permStates,
      t,
      tlns.loading,
    ]);

    // Select asset button state
    const selectAssetBtnState: PermissionInfo = useMemo(() => {
      // Block if general permStates is disabled...
      // eg: is loading, readonly, no broadcast perms
      if (permStates.disabled) {
        return {
          info: permStates.info,
          disabled: true,
        };
      }
      // Check editorial access permissions
      if (userIsAdmin || userHasEditorial) {
        return {
          info: undefined,
          disabled: false,
        };
      }
      // No editorial access permission
      return {
        info: t('blox.permissions.msg.required_short', {
          name: editorialAccessPerm,
        }),
        disabled: true,
      };
    }, [
      editorialAccessPerm,
      permStates.disabled,
      permStates.info,
      t,
      userHasEditorial,
      userIsAdmin,
    ]);

    // General confirm close strings/styles
    const confirmCloseProps: DefaultConfirmCloseProps = useMemo(() => {
      return {
        title: t('blox.modal.confirmClose'),
        content: t('blox.modal.confirmClosePrimary'),
        subtext: t('blox.modal.confirmCloseSecondary'),
        cancelTxt: t('blox.modal.cancelCloseBtn'),
        confirmTxt: t('blox.modal.confirmCloseBtn'),
        paperSx: {
          maxWidth: '400px',
        },
      };
    }, [t]);

    return (
      <Fragment>
        <DragModal
          open={msgModalOpen}
          shouldMask={isMasked}
          shouldInert={isMasked}
          disableEnforceFocus={true}
          preventBackdropClickClose={true}
          preventEscapeKeyClose={true}
          isDraggable={true}
          isResizable={true}
          isFullscreenable={true}
          initialWidth={600}
          initialHeight={'85vh'}
          minWidth={350}
          minHeight={350}
          shouldConfirmClose={confirmIfDirty}
          onClose={handleModalClose}
          defaultConfirmCloseProps={confirmCloseProps}
          showLoadingBar={anyLoading}
          header={
            <Box
              id="notifier-message-modal-header"
              data-testid="notifier-message-modal-header"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                overflow: 'auto',
                flexGrow: 1,
                gap: 1,
              }}
            >
              <NotificationsOutlinedIcon
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: bloxValues.iconSizeRegular,
                }}
              />
              <Typography
                sx={{
                  fontSize: bloxValues.xxlFontSizeRem,
                  color: theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
              >
                {uiTitle}
              </Typography>

              {isEditMode && <NotifierStatusBadge messageItem={fetchedMsg} />}
            </Box>
          }
          body={
            <FormProvider {...methods}>
              <ErrorAlerts
                headerText={tlns.error}
                severity="warning"
                buttonOnClick={() => getIdRefetch()}
                buttonText="Retry"
                buttonIcon={<ReplayIcon />}
                showDetails={true}
                sx={{
                  display: getIdError ? 'flex' : 'none',
                  margin: 1,
                  height: 'fit-content',
                }}
                errors={[
                  {
                    status: `${getIdError?.status || '\u2014'}`,
                    message: `${getIdError?.message || '\u2014'}`,
                    fullDetails: getIdError,
                  },
                ]}
              />

              {/* Confirm Delete Modal */}
              <SimpleConfirmDeleteModal
                open={confirmDeleteOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={closeConfirmDeleteModal}
                titleTxt={tlns.promptDelete}
                subTxt={tlns.cannotUndo}
                initialWidth={350}
                initialHeight={215}
                isDraggable={false}
                isResizable={false}
                isFullscreenable={false}
              />

              {/* Asset Select Modal */}
              <SearchModal
                resource="editorial"
                isOpen={assetModalOpen}
                closeModal={handleCloseAssetModal}
                selections={selectedAssets}
                setSelections={handleSetSelections}
                selectionMode="single"
                onAccept={handleAssetAccept}
              />

              <Box
                id="notifier-message-modal-main"
                data-testid="notifier-message-modal-main"
                sx={{
                  display: getIdError ? 'none' : 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  height: '100%',
                  minHeight: 0,
                  // fallback scroll only when needed (small screens)
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                <Box
                  id="notifier-message-modal-fields-top"
                  data-testid="notifier-message-modal-fields-top"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    flexShrink: 0,
                    overflow: 'visible',
                  }}
                >
                  <Box
                    id="notifier-message-target-row"
                    data-testid="notifier-message-target-row"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0,
                      width: '100%',
                    }}
                  >
                    <Box
                      id="notifier-message-target-inputs"
                      data-testid="notifier-message-target-inputs"
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 0.5,
                      }}
                    >
                      <TextField
                        data-testid="notifier-message-modal-target-title"
                        label={tlns.target}
                        placeholder={tlns.targetInfo}
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        margin="dense"
                        autoComplete="off"
                        disabled={permStates.disabled}
                        // Help/error text is in separate el below for
                        // better layout with the buttons when shown
                        helperText={null}
                        {...register('target', {
                          validate: {
                            required: (value) => {
                              // Note: there is additional validation required
                              // in handleFormSave function to ensure either is
                              // a valid url or both asset_uuid and asset_app exist.

                              const currentApp = getValues('asset_app') || '';
                              const currentUuid = getValues('asset_uuid') || '';
                              // If asset fields are set, skip required validation
                              // as validation will occur when the user submits
                              // the form and handleFormSave will determine if
                              // target contains valid url or to use asset fields.
                              if (currentApp && currentUuid) {
                                return true;
                              }

                              // Ensure field has a value set...
                              const trimmed = value?.trim() ?? '';
                              if (!trimmed) {
                                return t('blox.validation.value_required', {
                                  label: tlns.target,
                                });
                              }

                              return true;
                            },
                          },
                        })}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />

                      <Tooltip title={selectAssetBtnState?.info}>
                        <span>
                          <Button
                            variant="outlined"
                            startIcon={<SearchIcon />}
                            onClick={handleOpenAssetModal}
                            disabled={selectAssetBtnState.disabled}
                            sx={{
                              fontSize: bloxValues.smallFontSizeRem,
                              height: '43px',
                              mt: 1,
                            }}
                          >
                            {tlns.search}
                          </Button>
                        </span>
                      </Tooltip>

                      {wAssetUuid && (
                        <Tooltip title={tlns.openInNewWindow}>
                          <IconButton
                            href={viewAssetHref}
                            disabled={!wAssetUuid}
                            target="_blank"
                            sx={{ mt: 1 }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Help/error message for target field */}
                    {fsTarget?.error?.message && (
                      <Typography sx={getInputErrorTextSx}>
                        {String(fsTarget?.error?.message)}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    data-testid="notifier-message-modal-title-input"
                    label={tlns.title}
                    placeholder=""
                    variant="outlined"
                    size="small"
                    margin="dense"
                    fullWidth={true}
                    autoComplete="off"
                    disabled={permStates.disabled}
                    error={Boolean(fsTitle?.error)}
                    helperText={
                      <Stack>
                        {fsTitle?.error?.message && (
                          <Typography sx={getInputErrorTextSx}>
                            {String(fsTitle?.error?.message)}
                          </Typography>
                        )}
                      </Stack>
                    }
                    {...register('title', {
                      validate: {
                        // In blue this field is optional...
                        // changes requested to make required in nxt ui
                        required: (value) => {
                          const trimmedValue = value?.trim() ?? '';
                          if (!trimmedValue) {
                            return t('blox.validation.value_invalid', {
                              label: tlns.title,
                            });
                          }
                          if (!value || value?.length === 0) {
                            return t('blox.validation.value_empty', {
                              label: tlns.title,
                            });
                          }
                          return true;
                        },
                      },
                    })}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />

                  <TextField
                    data-testid="notifier-message-modal-message-input"
                    label={msgLabel} // Message * (0/250)
                    placeholder=""
                    autoComplete="off"
                    disabled={permStates.disabled}
                    fullWidth={true}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    multiline={true}
                    minRows={3}
                    maxRows={6}
                    error={Boolean(fsMessage?.error)}
                    helperText={
                      <Stack>
                        {fsMessage?.error?.message && (
                          <Typography sx={getInputErrorTextSx}>
                            {String(fsMessage?.error?.message)}
                          </Typography>
                        )}
                      </Stack>
                    }
                    {...register('message', {
                      validate: {
                        required: (value) => {
                          const trimmedValue = value?.trim() ?? '';
                          if (!trimmedValue) {
                            return t('blox.validation.value_invalid', {
                              label: tlns.message,
                            });
                          }
                          if (!value || value?.length === 0) {
                            return t('blox.validation.value_empty', {
                              label: tlns.message,
                            });
                          }
                          if (value?.length > NOTIFIER_MSG_MAX_LENGTH) {
                            return t('blox.validation.value_too_long', {
                              label: tlns.message,
                            });
                          }
                          return true;
                        },
                      },
                    })}
                    slotProps={{
                      htmlInput: {
                        maxLength: NOTIFIER_MSG_MAX_LENGTH,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />

                  <Controller
                    name="send_date"
                    control={control}
                    rules={{
                      validate: (value) => {
                        return validateSendDate(value);
                      },
                    }}
                    render={({ field, fieldState, formState }) => {
                      // For display we set the timezone prop to site tz
                      // https://mui.com/x/react-date-pickers/timezone/
                      return (
                        <DateTimePicker
                          label={tlns.scheduledDateAndTime}
                          data-testid="notifier-message-modal-send-date-input"
                          disabled={permStates.disabled}
                          maxDate={safeMaxDateTime}
                          minDate={assetStartDayJs}
                          timeSteps={{ hours: 1, minutes: 5, seconds: 0 }}
                          disablePast={true}
                          reduceAnimations={true}
                          value={sendDateState}
                          timezone={siteTimeZone} // display tz
                          onChange={(value, context) => {
                            // Always update controlled dayjs state
                            setSendDateState(value);

                            // Partial date input (no RHF, local state update)
                            if (context.validationError === 'invalidDate') {
                              setError('send_date', {
                                type: 'manual',
                                message: t('blox.validation.value_incomplete', {
                                  label: tlns.date,
                                }),
                              });
                              return;
                            }

                            /**
                             * Update RHF only when valid date or cleared
                             * to prevents clearing on partial input,
                             * onChanges will trigger rhf validation.
                             */

                            // Null is fully cleared
                            if (value === null) {
                              field.onChange(null);
                              return;
                            }

                            // Incoming dayjs value is in site tz
                            // convert to UTC string for form storage
                            if (value && value.isValid()) {
                              const utcValue = value.utc().toISOString();
                              field.onChange(utcValue);
                              return;
                            }
                          }}
                          slotProps={{
                            actionBar: {
                              actions: ['clear', 'cancel', 'accept'],
                            },
                            textField: {
                              variant: 'outlined',
                              size: 'small',
                              autoComplete: 'off',
                              autoCorrect: 'off',
                              autoCapitalize: 'off',
                              spellCheck: 'false',
                              InputLabelProps: { shrink: true },
                              fullWidth: true,
                              error: Boolean(fieldState?.error),
                              helperText: dateHelpText,
                              inputProps: {
                                'data-testid':
                                  'notifier-message-modal-send-date-input-textfield',
                              },
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Box>

                <Box
                  id="notifier-message-modal-channels-bottom"
                  data-testid="notifier-message-modal-channels-bottom"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    flex: 1,
                    minWidth: 0,
                    minHeight: 225,
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: bloxValues.smallFontSizeRem,
                      fontWeight: bloxValues.fontWeightRegular,
                      color: theme.palette.text.secondary,
                      pl: 1.5,
                    }}
                  >
                    {tlns.channels}
                  </Typography>

                  <Controller
                    name="channels"
                    control={control}
                    rules={{
                      validate: (value) => {
                        // should always be an array type
                        // is an array of strings (channel ids)
                        if (!Array.isArray(value)) {
                          return tlns.formInvalid;
                        }
                        // note: extra validation runs on publish action
                        // see schedulePostBtnState / handleFormSave
                        return true;
                      },
                    }}
                    render={({ field, fieldState, formState }) => {
                      const selectedIds = field?.value || [];

                      // channels loading, no data yet
                      if (isChannelsInitialLoading) {
                        return (
                          <SkeletonBox
                            text={tlns.loading}
                            width="100%"
                            height="55px"
                          />
                        );
                      }

                      // no channels returned from api
                      if (noChannels) {
                        return (
                          <Typography
                            id="notifier-message-modal-channels-empty-loading"
                            data-testid="notifier-message-modal-channels-empty-loading"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              fontSize: bloxValues.regularFontSizeRem,
                              color: theme.palette.text.disabled,
                              m: 1,
                              p: 1,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor: theme.palette.action.hover,
                            }}
                          >
                            {tlns.noChannels}
                          </Typography>
                        );
                      }

                      // list of channels to select
                      return (
                        <List
                          id="notifier-message-modal-channels-list"
                          data-testid="notifier-message-modal-channels-list"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            gap: 1,
                            p: 0.5,
                            borderRadius: 1,
                            border: fsChannels?.error
                              ? `1px solid ${theme.palette.error.main}`
                              : `1px solid ${theme.palette.action.selected}`,
                            // primary scroll in normal cases
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            // ensures list can shrink/grow inside the min wrapper
                            flex: 1,
                            minHeight: 0,
                          }}
                        >
                          {flatChannels?.map((channel, index) => {
                            const reactKey = `${channel?.id}`;
                            const itemId = channel?.id ?? '';
                            const channelName = channel?.name ?? '';
                            const channelDisabled = channel?.disabled || false;
                            const isSelected = selectedIds?.includes(itemId) || false;
                            const ariaId = `channel-checkbox-${reactKey}`;
                            const isLastItem = Boolean(
                              index === flatChannels?.length - 1,
                            );

                            // add or remove from selected ids
                            const handleToggle = (
                              e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                            ) => {
                              e?.preventDefault();
                              const next = isSelected
                                ? selectedIds.filter((x) => x !== itemId)
                                : [...selectedIds, itemId];
                              field.onChange(next);
                              trigger('channels');
                            };

                            return (
                              <Fragment key={reactKey}>
                                <ListItem
                                  data-testid="channel-list-item"
                                  aria-checked={isSelected}
                                  sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    p: 0,
                                  }}
                                >
                                  <ListItemButton
                                    onClick={handleToggle}
                                    data-testid="channel-list-button"
                                    disabled={permStates.disabled}
                                    role={undefined}
                                    sx={{
                                      px: 0,
                                      py: 1,
                                      m: 0,
                                    }}
                                  >
                                    <Checkbox
                                      data-testid="channel-list-checkbox"
                                      size="small"
                                      disabled={permStates.disabled}
                                      disableRipple={true}
                                      checked={isSelected}
                                      tabIndex={-1}
                                      sx={{
                                        margin: 0,
                                        py: 0,
                                        px: 0.5,
                                        fontSize: 18,
                                        '& svg': {
                                          fontSize: 18,
                                        },
                                      }}
                                      aria-labelledby={ariaId}
                                    />

                                    <ListItemText
                                      id={ariaId}
                                      data-testid="channel-list-info"
                                      disableTypography={true}
                                    >
                                      <Box
                                        data-testid="channel-list-text-row"
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: bloxValues.regularFontSizeRem,
                                            color: theme.palette.text.primary,
                                          }}
                                        >
                                          {channelName}
                                        </Typography>

                                        {channelDisabled && (
                                          <Typography
                                            sx={{
                                              fontSize: bloxValues.tinyFontSizeRem,
                                              color: theme.palette.text.disabled,
                                            }}
                                          >
                                            ({tlns.channelDisabled})
                                          </Typography>
                                        )}
                                      </Box>
                                    </ListItemText>
                                  </ListItemButton>
                                </ListItem>
                                {isLastItem && (
                                  <IntersectionLoader
                                    hasNextPage={hasNextPageChannels}
                                    isFetching={isFetchingNextPageChannels}
                                    fetchNextPage={fetchNextPageChannels}
                                    endOfResultsMessage={tlns.endOfResults}
                                    ixRef={ixRef}
                                    entry={entry}
                                    compact={true}
                                  />
                                )}
                              </Fragment>
                            );
                          })}
                        </List>
                      );
                    }}
                  />
                  {fsChannels?.error?.message && (
                    <Typography
                      sx={{
                        fontSize: bloxValues.tinyFontSizeRem,
                        color: theme.palette.error.main,
                      }}
                    >
                      {String(fsChannels?.error?.message)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </FormProvider>
          }
          footer={
            <Box
              id="notifier-message-modal-footer"
              data-testid="notifier-message-modal-footer"
              sx={{
                display: getIdError ? 'none' : 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                id="notifier-message-modal-footer-left"
                data-testid="notifier-message-modal-footer-left"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                }}
              >
                {isEditMode && (
                  <Tooltip title={deleteBtnState?.info}>
                    <span>
                      <Button
                        data-testid="notifier-message-modal-delete-button"
                        variant="text"
                        color="error"
                        size="medium"
                        onClick={handleDeleteMenuClick}
                        disableElevation={true}
                        disabled={deleteBtnState?.disabled}
                      >
                        {tlns.delete}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {showViewLogs && messageId && (
                  <Button
                    data-testid="notifier-message-modal-view-logs-button"
                    variant="text"
                    color="primary"
                    size="medium"
                    onClick={() => handleOpenLogsModal(messageId)}
                    disableElevation={true}
                  >
                    {t('blox.notifier.logs.action')}
                  </Button>
                )}
              </Box>

              <Box
                id="notifier-message-modal-footer-right"
                data-testid="notifier-message-modal-footer-right"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                }}
              >
                <Tooltip title={saveDraftBtnState?.info}>
                  <span>
                    <Button
                      data-testid="notifier-message-modal-save-draft-button"
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={(e) => handleFormSave(e, 'draft')}
                      disableElevation={true}
                      disabled={saveDraftBtnState?.disabled}
                    >
                      {tlns.saveDraft}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title={schedulePostBtnState?.info}>
                  <span>
                    <Button
                      data-testid="notifier-message-modal-schedule-button"
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={(e) => handleFormSave(e, 'publish')}
                      disableElevation={true}
                      disabled={schedulePostBtnState?.disabled}
                    >
                      {tlns.schedulePost}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          }
        />
      </Fragment>
    );
  },
);
NotifierMessageModal.displayName = 'NotifierMessageModal';
